import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { useTranslation } from '@unifyapps/i18n/client';
import { useLookup } from '@unifyapps/network/generated/lookup-rest-api/lookup-rest-api';
import type { LookupByQueryRequest } from '@unifyapps/network/generated/models/lookupByQueryRequest';
import { LookupByQueryRequestType } from '@unifyapps/network/generated/models/lookupByQueryRequestType';
import type { NotificationPreference } from '@unifyapps/network/generated/models/notificationPreference';
import {
  getFindNotificationPreferenceQueryKey,
  useFindNotificationPreference,
  useSaveNotificationPreference,
} from '@unifyapps/network/generated/notification-preferences-rest-api/notification-preferences-rest-api';
import { AccordionGroup } from '@unifyapps/ui/components/Accordion';
import { Button } from '@unifyapps/ui/components/Button';
import { Stack } from '@unifyapps/ui/components/Stack';
import { useCallback, useMemo } from 'react';
import useMutationHelpers from '../../../../hooks/useMutationHelpers';
import type { WithRequired } from '../../../../types';
import AsyncView from '../../../AsyncView';
import {
  getPreferencesFromState,
  useNotificationPreferencesState,
} from '../../hooks/useNotificationPreferencesState';
import type {
  NotificationEventType,
  NotificationEventTypesByQueryResponse,
  NotificationGroupEntity,
  NotificationGroupTypesByQueryResponse,
} from '../../types';
import { NotificationEvents } from '../../utils/notificationEvents';
import { GroupSettings } from './GroupSettings';

type Data = {
  groups?: NotificationGroupEntity[];
  notificationPreferences?: NotificationPreference;
  events?: NotificationEventType[];
};

type Props = {
  initialPreferences: NotificationPreference | undefined;
  onBack: () => void;
  groups: NotificationGroupEntity[];
  events: NotificationEventType[];
};

function Settings({ initialPreferences, onBack, groups, events }: Props) {
  const { t } = useTranslation(['common']);

  const channelTypes = useMemo(() => [{ id: 'IN_APP', name: t('common:Actions.SelectAll') }], [t]);

  const { preferences, onToggleGroup, onToggleEvent } = useNotificationPreferencesState({
    initialPreferences,
    events,
    channelTypes,
  });

  const { onInvalidateQueriesAsync, onShowErrorSnackbar, onShowSuccessSnackbar } =
    useMutationHelpers();

  const { mutate: saveNotificationPreference } = useSaveNotificationPreference({
    mutation: {
      onSuccess: async () => {
        await onInvalidateQueriesAsync([getFindNotificationPreferenceQueryKey()]);
        onShowSuccessSnackbar({
          title: t('common:NotificationPreferencesUpdatedSuccessfully'),
        });
        onBack();
      },
      onError: onShowErrorSnackbar,
    },
  });

  const onSave = useCallback(() => {
    saveNotificationPreference({
      data: { eventVsPreferences: getPreferencesFromState(preferences) },
    });
  }, [preferences, saveNotificationPreference]);

  const groupedEvents = useMemo(() => NotificationEvents.getGroupedEvents({ events }), [events]);

  return (
    <Stack className="pt-2xl bg-primary h-full w-full" direction="column">
      <AccordionGroup className="gap-3xl px-2xl" size="sm">
        {groups.map((group) =>
          groupedEvents[group.id]?.length > 0 ? (
            <GroupSettings
              channelTypes={channelTypes}
              events={groupedEvents[group.id]}
              groupId={group.id}
              key={group.id}
              name={group.properties.name}
              onToggleEvent={onToggleEvent}
              onToggleGroup={onToggleGroup}
              preferences={preferences[group.id]}
            />
          ) : null,
        )}
      </AccordionGroup>
      <Stack
        className="gap-lg py-lg px-xl border-tertiary bg-primary sticky bottom-0 justify-end border-t"
        direction="row"
      >
        <Button color="brand" onClick={onBack} size="md" variant="outline">
          {t('common:Actions.Cancel')}
        </Button>
        <Button color="brand" onClick={onSave} size="md" variant="solid">
          {t('common:Actions.Save')}
        </Button>
      </Stack>
    </Stack>
  );
}

function SettingsContainer({ onBack }: { onBack: () => void }) {
  const {
    data: notificationPreferences,
    isLoading: isNotificatonPreferencesLoading,
    error: notificationPreferencesError,
  } = useFindNotificationPreference();

  const {
    data: groups,
    isLoading: areGroupsLoading,
    error: groupsError,
  } = useLookup<NotificationGroupTypesByQueryResponse>({
    type: LookupByQueryRequestType.ByQuery,
    lookupType: 'ENTITY',
    options: { entity_type: UAEntityTypes.NotificationGroup },
  });

  const {
    data: eventTypesData,
    isLoading: isEventTypesLoading,
    error: eventTypesError,
  } = useLookup<NotificationEventTypesByQueryResponse>({
    type: LookupByQueryRequestType.ByQuery,
    lookupType: 'ENTITY',
    options: {
      entity_type: UAEntityTypes.NotificationEventType,
    },
  } as LookupByQueryRequest);

  const events = useMemo(
    () =>
      eventTypesData?.response?.objects?.map(({ properties, id }) => ({
        ...properties,
        //eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- known
        id: id!,
      })) ?? [],
    [eventTypesData?.response?.objects],
  );

  const renderData = useCallback(
    ({
      groups: _groups,
      notificationPreferences: _notificationPreferences,
      events: _events,
    }: WithRequired<Data, 'groups' | 'notificationPreferences' | 'events'>) => (
      <Settings
        events={_events}
        groups={_groups}
        initialPreferences={_notificationPreferences}
        onBack={onBack}
      />
    ),
    [onBack],
  );

  const data = useMemo(
    () => ({ groups: groups?.response?.objects, notificationPreferences, events }),
    [groups, notificationPreferences, events],
  );

  const error = notificationPreferencesError ?? groupsError ?? eventTypesError,
    isLoading = isNotificatonPreferencesLoading || areGroupsLoading || isEventTypesLoading;

  return (
    <AsyncView<Data, Error>
      data={data}
      error={error}
      isLoading={isLoading}
      renderData={renderData}
    />
  );
}

export { SettingsContainer as Settings };
